import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { useFormikContext } from 'formik';
import { Preloader } from '@/components/Preloader';
import { prettyFloatMoney } from '@/helpers/utils';
import { MyTooltip } from '@/webkit/MyTooltip';
import { useGetBonusGroup } from '@/query/queries/marketing/useGetBonusGroup';
import { useGetBalance } from '@/query/queries/cash/useGetBalance';
import { useGetInvestingStatus } from '@/query/queries/investing/useGetInvestingStatus';
import { useGetAllocateInfo } from '@/query/queries/investing/useGetAllocateInfo';
import {
  EMPTY_DASH,
  LOAN_GROUPS_IDS as LOAN_GROUP_IDS,
} from '@/helpers/constants';
import { TOOLTIP_TEXT as TOOLTIP_TEXT_T } from '../../../tooltip';
import { Toggle } from '../Toggle';
import { Button } from '../Button';
import IconTooltip from './icon_tooltip.svg';
import s from './index.module.scss';

export const Result = ({ data }) => {
  const { values, setFieldValue } = useFormikContext();
  const { data: result, isLoading } = data;
  const { t } = useTranslation('calculator');
  const { data: bonusGroup } = useGetBonusGroup();
  const { data: status } = useGetInvestingStatus();
  const { data: allocated } = useGetAllocateInfo();
  const TOOLTIP_TEXT = TOOLTIP_TEXT_T(
    t,
    prettyFloatMoney(bonusGroup?.bonus_amount),
    prettyFloatMoney(bonusGroup?.investment_amount),
  );
  const { data: balance } = useGetBalance();

  const currentBonuses = balance?.available_bonus_amount || 0;
  const allocatedBonuses = allocated?.allocated_bonus_amount || 0;
  const bonuses = currentBonuses + allocatedBonuses;

  const tooltipIcon = (
    <img
      src={IconTooltip}
      alt="tooltip"
      width={20}
      height={20}
      style={{ minWidth: '20px', display: 'block' }}
    />
  );

  return (
    <div className={s.wrapper}>
      <div className={s.container}>
        <div className={s.content}>
          <div className={s.row}>
            <div className="f-small-regular">
              {t('growth.total_amount_returned')}
            </div>
            <div className={s.row_inner}>
              {isLoading ? (
                <Preloader style={{ width: '20px' }} />
              ) : result ? (
                <span className="f-small-accent">
                  {prettyFloatMoney(result.total_amount)}
                </span>
              ) : (
                EMPTY_DASH
              )}
              <MyTooltip
                theme="dark"
                renderContent={() => (
                  <div className={s.tooltip}>
                    <span className="f-small-regular">
                      {TOOLTIP_TEXT.total_amount_returned}
                    </span>
                  </div>
                )}
                renderElement={() => tooltipIcon}
              />
            </div>
          </div>
          <div className={s.row}>
            <div className="f-small-regular">
              {t('growth.total_interest_earned')}
            </div>
            <div className={s.row_inner}>
              {isLoading ? (
                <Preloader style={{ width: '20px' }} />
              ) : result ? (
                <span className="f-small-accent">
                  {prettyFloatMoney(result.total_interest)}
                </span>
              ) : (
                EMPTY_DASH
              )}
              <MyTooltip
                theme="dark"
                renderElement={() => tooltipIcon}
                renderContent={() => (
                  <div className={s.tooltip}>
                    <span className="f-small-regular">
                      {TOOLTIP_TEXT.total_interest_earned}
                    </span>
                  </div>
                )}
              />
            </div>
          </div>
          <div className={s.row}>
            <div className="f-small-regular">
              {t('growth.projected_annual_return_irr')}
            </div>
            <div className={s.row_inner}>
              {isLoading ? (
                <Preloader style={{ width: '20px' }} />
              ) : result ? (
                <span className="f-small-accent">{`${Number(result.irr)}%`}</span>
              ) : (
                EMPTY_DASH
              )}
              <MyTooltip
                theme="dark"
                renderElement={() => tooltipIcon}
                renderContent={() => (
                  <div className={s.tooltip}>
                    <span className="f-small-regular">
                      {TOOLTIP_TEXT.projected_annual_return}
                    </span>
                  </div>
                )}
              />
            </div>
          </div>
          <div className={s.row}>
            <div className="f-small-regular">
              {t('growth.total_contributions')}
            </div>
            <div className={s.row_inner}>
              {isLoading ? (
                <Preloader style={{ width: '20px' }} />
              ) : result ? (
                <span className="f-small-accent">
                  {prettyFloatMoney(result.total_contributions)}
                </span>
              ) : (
                EMPTY_DASH
              )}
              <MyTooltip
                theme="dark"
                renderElement={() => tooltipIcon}
                renderContent={() => (
                  <div className={s.tooltip}>
                    <span className="f-small-regular">
                      {TOOLTIP_TEXT.total_contributions}
                    </span>
                  </div>
                )}
              />
            </div>
          </div>
          {values.group_id === LOAN_GROUP_IDS.conservative.id &&
            (bonuses || bonusGroup) && (
              <>
                <div
                  className={classnames(
                    s.row,
                    !values.is_bonuses_included && s.row_last,
                  )}
                >
                  <div className="f-small-regular">
                    {t('growth.turn_on_bonuses')}
                  </div>
                  <div>
                    <Toggle
                      checked={values.is_bonuses_included}
                      onChange={checked =>
                        setFieldValue('is_bonuses_included', checked)
                      }
                    />
                  </div>
                </div>
                <div
                  className={classnames(
                    s.dropdown,
                    values.is_bonuses_included && s.dropdown_open,
                  )}
                >
                  <div className={s.row}>
                    <div className="f-small-regular">
                      {t('growth.your_current_bonus')}
                    </div>
                    <div className="f-small-accent">
                      {prettyFloatMoney(bonuses)}
                    </div>
                  </div>
                  {bonusGroup && status && (
                    <div className={s.row}>
                      <div className="f-small-regular">
                        {t(
                          status.is_active
                            ? 'growth.current_round_offer'
                            : 'growth.next_round_offer',
                          {
                            x: prettyFloatMoney(bonusGroup.bonus_amount),
                            y: prettyFloatMoney(bonusGroup.investment_amount),
                          },
                        )}
                      </div>
                      <div>
                        <MyTooltip
                          theme="dark"
                          renderElement={() => tooltipIcon}
                          renderContent={() => (
                            <div className={s.tooltip}>
                              <span className="f-small-regular">
                                {TOOLTIP_TEXT.extra_for_every_5}
                              </span>
                            </div>
                          )}
                        />
                      </div>
                    </div>
                  )}
                  {bonusGroup && (
                    <div className={s.row}>
                      <div className="f-small-regular">
                        {t('growth.total_bonus')}
                      </div>
                      <div>
                        {isLoading ? (
                          <Preloader style={{ width: '20px' }} />
                        ) : result ? (
                          <span className="f-small-accent">
                            {prettyFloatMoney(result.total_bonus)}
                          </span>
                        ) : (
                          EMPTY_DASH
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
        </div>
      </div>
      <Button />
    </div>
  );
};
